import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, type UploadStatus } from '@painpal/contracts';

export type UploadPhase = 'rejected' | 'requesting' | 'uploading' | 'processing' | 'processed' | 'failed';

export interface UploadItem {
  id: string;
  file: File;
  name: string;
  phase: UploadPhase;
  key: string | null;
  activityId: string | null;
  message: string | null;
}

export type UploadAction =
  | { type: 'add'; items: UploadItem[] }
  | { type: 'clearFinished' }
  | { type: 'ticket'; id: string; key: string }
  | { type: 'uploaded'; id: string }
  | { type: 'status'; id: string; status: UploadStatus }
  | { type: 'error'; id: string; message: string }
  | { type: 'retry'; id: string }
  | { type: 'resume'; id: string };

type ItemAction = Exclude<UploadAction, { type: 'add' } | { type: 'clearFinished' }>;

export function validateFile(file: Pick<File, 'name' | 'size'>): string | null {
  if (!file.name.toLowerCase().endsWith('.fit')) return 'Only .FIT files can be uploaded';
  if (file.size > MAX_UPLOAD_BYTES) return `File is larger than ${MAX_UPLOAD_MB} MB`;
  return null;
}

export function filesOf(list: FileList | null): File[] {
  return list === null ? [] : Array.from(list);
}

export function createItems(files: File[], makeId: () => string): UploadItem[] {
  return files.map((file) => {
    const rejection = validateFile(file);
    return {
      id: makeId(), file, name: file.name, phase: rejection === null ? 'requesting' : 'rejected',
      key: null, activityId: null, message: rejection,
    };
  });
}

export function isPolling(item: UploadItem): item is UploadItem & { key: string } {
  return item.phase === 'processing' && item.key !== null;
}

function fromStatus(item: UploadItem, status: UploadStatus): UploadItem {
  if (status.status === 'pending') return { ...item, activityId: status.activityId };
  if (status.status === 'failed') {
    return { ...item, phase: 'failed', activityId: status.activityId, message: status.error ?? 'Processing failed' };
  }
  return { ...item, phase: 'processed', activityId: status.activityId, message: null };
}

function applyToItem(item: UploadItem, action: ItemAction): UploadItem {
  switch (action.type) {
    case 'ticket':
      return { ...item, phase: 'uploading', key: action.key, message: null };
    case 'uploaded':
      return { ...item, phase: 'processing' };
    case 'status':
      return fromStatus(item, action.status);
    case 'error':
      return { ...item, phase: 'failed', message: action.message };
    case 'retry':
      return { ...item, phase: 'requesting', key: item.activityId === null ? null : item.key, message: null };
    case 'resume':
      return { ...item, phase: 'processing' };
  }
}

export function uploadReducer(state: UploadItem[], action: UploadAction): UploadItem[] {
  if (action.type === 'add') return [...action.items, ...state];
  if (action.type === 'clearFinished') return state.filter((i) => i.phase !== 'processed' && i.phase !== 'rejected');
  return state.map((item) => (item.id === action.id ? applyToItem(item, action) : item));
}
